/**
 * Portal access requests tab (tracker item 45, phase 4): verified registrations that could not be linked
 * to a customer automatically. Each card shows the evidence the backend recorded (whether a code was
 * entered and which proof fields matched which candidate customer, never the typed values) and lets the
 * office link the login to a customer (a candidate in one step, or any customer from the picker) after an
 * explicit confirmation, or decline it. A linked login sees that customer's invoices and jobs, so every
 * link asks "are you sure" first.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IRequestsApi } from '../../api';
import { PortalAccessRequest, PortalAccessRequestFilter } from '../../api/types';
import { CustomerPicker } from '../../components/CustomerPicker';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { RequestPager } from '../../components/RequestPager';
import { Toast } from '../../components/Toast';
import {
  ACCESS_FILTERS, REVIEW_REASON_LABELS, evidenceFields, isStaleRequestError, matchedCount, requestActionError, statusBadgeClass, whenLabel,
} from './requestsUi';
import { INBOX_PAGE_SIZE, useInboxList } from './useInboxList';

type Action = { kind: 'link'; customerId: string; customerName: string } | { kind: 'pick' } | { kind: 'decline' } | null;

function AccessCard({ request, api, onChanged }: { request: PortalAccessRequest; api: IRequestsApi; onChanged: (message?: string) => void }) {
  const [action, setAction] = useState<Action>(null);
  const [pickedId, setPickedId] = useState('');
  const [pickedName, setPickedName] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = request.status === 'PENDING';

  const run = async (work: () => Promise<unknown>, success: string, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onChanged(success);
    } catch (cause) {
      setError(requestActionError(cause, failure));
      if (isStaleRequestError(cause)) onChanged();
    } finally {
      setBusy(false);
    }
  };

  const link = (customerId: string) =>
    run(() => api.decidePortalAccessRequest(request.id, { action: 'link', customerId }), `${request.user.email} is now linked. They can sign in and see that customer's account.`, 'Could not link the login');
  const decline = () => {
    if (reason.length > 500) { setError('The reason can be at most 500 characters'); return; }
    return run(
      () => api.decidePortalAccessRequest(request.id, { action: 'decline', ...(reason.trim() ? { reason: reason.trim() } : {}) }),
      'Access request declined.',
      'Could not decline the request',
    );
  };

  return (
    <li className="rounded-lg bg-white p-4 shadow" data-testid="access-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words font-semibold text-gray-900">{request.user.name || request.user.email}</p>
          <p className="break-all text-sm text-gray-600">{request.user.email}{request.user.phone ? ` · ${request.user.phone}` : ''}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {request.flagged && <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800" data-testid="flagged">Repeated failed matches</span>}
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(request.status)}`}>{request.status.charAt(0) + request.status.slice(1).toLowerCase()}</span>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500">Registered {whenLabel(request.createdAt)}</p>

      <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm">
        <p className="font-semibold text-gray-800">Why it was not linked automatically</p>
        <p className="mt-1 text-gray-700">{request.reason ? REVIEW_REASON_LABELS[request.reason] : 'Not recorded.'}</p>
        <p className="mt-1 text-gray-600">{request.codeGiven ? 'A registration code was entered.' : 'No registration code was entered.'}</p>
        <p className="mt-3 font-semibold text-gray-800">Customers considered</p>
        {request.candidates.length === 0 ? (
          <p className="mt-1 text-gray-600">None: nobody matched this email address.</p>
        ) : (
          <ul className="mt-1 space-y-2" aria-label="Candidate customers">
            {request.candidates.map((candidate) => {
              const fields = evidenceFields(candidate.matched);
              return (
                <li key={candidate.customerId} className="rounded border border-gray-200 bg-white p-2" data-testid="candidate">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Link to={`/customers/${candidate.customerId}`} className="break-words font-medium text-blue-700 hover:underline">
                      {candidate.customerName ?? 'Unknown customer'}
                    </Link>
                    {pending && (
                      <button
                        type="button" className={secondaryButtonClass} disabled={busy}
                        onClick={() => { setAction({ kind: 'link', customerId: candidate.customerId, customerName: candidate.customerName ?? 'this customer' }); setError(null); }}
                        aria-label={`Link to ${candidate.customerName ?? 'this customer'}`}
                      >
                        Link to this customer
                      </button>
                    )}
                  </div>
                  {fields ? (
                    <ul className="mt-1 flex flex-wrap gap-2" aria-label={`Evidence for ${candidate.customerName ?? 'this customer'}`}>
                      {fields.map((f) => (
                        <li key={f.key} className={`rounded px-2 py-0.5 text-xs font-semibold ${f.ok ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {f.label}: {f.ok ? 'matched' : 'no match'}
                        </li>
                      ))}
                      <li className="px-1 py-0.5 text-xs text-gray-600">{matchedCount(candidate.matched)} of 3 matched (2 are needed)</li>
                    </ul>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">No proof details were checked for this customer.</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {!pending && (
        <p className="mt-3 text-sm text-gray-700" data-testid="access-outcome">
          {request.status === 'LINKED'
            ? <>Linked to <Link to={`/customers/${request.linkedCustomerId}`} className="text-blue-700 hover:underline">{request.candidates.find((c) => c.customerId === request.linkedCustomerId)?.customerName ?? 'the customer'}</Link>{request.decidedAt ? ` on ${whenLabel(request.decidedAt)}` : ''}.</>
            : <>Declined{request.decidedAt ? ` on ${whenLabel(request.decidedAt)}` : ''}{request.declineReason ? `: ${request.declineReason}` : '.'}</>}
        </p>
      )}

      {pending && action === null && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button type="button" className={secondaryButtonClass} onClick={() => { setAction({ kind: 'pick' }); setError(null); }}>Link to a different customer</button>
          <button type="button" className={secondaryButtonClass} onClick={() => { setAction({ kind: 'decline' }); setError(null); }}>Decline</button>
        </div>
      )}

      {pending && action?.kind === 'pick' && (
        <div className="mt-4 space-y-3 rounded-md bg-gray-50 p-3">
          <CustomerPicker
            id={`pick-${request.id}`} label="Customer" value={pickedId} disabled={busy}
            preferredIds={request.candidates.map((c) => c.customerId)}
            onChange={(id, customer) => { setPickedId(id); setPickedName(customer?.name ?? ''); }}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" className={primaryButtonClass} disabled={busy || !pickedId} onClick={() => setAction({ kind: 'link', customerId: pickedId, customerName: pickedName || 'this customer' })}>Continue</button>
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setAction(null)}>Cancel</button>
          </div>
        </div>
      )}

      {pending && action?.kind === 'link' && (
        <div className="mt-4 space-y-3 rounded-md bg-amber-50 p-3 ring-1 ring-amber-200" role="group" aria-label="Confirm link">
          <p className="text-sm text-amber-900">
            Link <strong>{request.user.email}</strong> to <strong>{action.customerName}</strong>? This login will see that customer's invoices, balance, jobs and
            payment options. Only do this when you are sure the person is the customer.
          </p>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void link(action.customerId)}>{busy ? 'Linking…' : 'Yes, link this login'}</button>
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setAction(null); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {pending && action?.kind === 'decline' && (
        <form className="mt-4 space-y-3 rounded-md bg-gray-50 p-3" aria-label="Decline access request" onSubmit={(e) => { e.preventDefault(); void decline(); }}>
          <div>
            <label htmlFor={`ac-reason-${request.id}`} className={labelClass}>Reason (optional, kept in the audit log)</label>
            <textarea id={`ac-reason-${request.id}`} className={inputClass} rows={2} maxLength={500} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="submit" className={primaryButtonClass} disabled={busy}>{busy ? 'Declining…' : 'Decline request'}</button>
            <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => { setAction(null); setError(null); }}>Cancel</button>
          </div>
        </form>
      )}
      {pending && action?.kind !== 'link' && action?.kind !== 'decline' && error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
    </li>
  );
}

export function AccessRequestsTab() {
  const api = useMemo<IRequestsApi>(() => createAPIClient(), []);
  const [status, setStatus] = useState<PortalAccessRequestFilter>('PENDING');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);
  const fetchPage = useCallback(
    (pageNo: number) => api.listPortalAccessRequests({ status, page: pageNo, limit: INBOX_PAGE_SIZE }),
    [api, status],
  );
  const { rows, pagination, loading, error, reload } = useInboxList(fetchPage, page, 'Could not load portal access requests');

  const changed = (message?: string) => {
    setNotice(message ?? null);
    void reload();
  };

  return (
    <section aria-label="Portal access requests">
      <p className="mb-3 text-sm text-gray-600">
        People who registered on the portal and confirmed their email, but could not be matched to a customer automatically. Until you link them they see no customer data.
      </p>
      <div className="mb-4 max-w-xs">
        <label htmlFor="access-status" className={labelClass}>Show</label>
        <select
          id="access-status" className={`${inputClass} min-h-[44px]`} value={status}
          onChange={(e) => { setStatus(e.target.value as PortalAccessRequestFilter); setPage(1); setNotice(null); }}
        >
          {ACCESS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {notice && <Toast kind="success">{notice}</Toast>}
      {error && (
        <div>
          <Toast kind="error">{error}</Toast>
          <button type="button" className="mb-3 text-sm font-semibold text-blue-700 hover:underline min-h-[44px]" onClick={() => void reload()}>Try again</button>
        </div>
      )}
      {loading && rows.length === 0 && !error && <p role="status" className="text-gray-600">Loading portal access requests…</p>}
      {!loading && !error && rows.length === 0 && (
        <p className="rounded-lg bg-white p-6 text-center text-gray-600 shadow" data-testid="empty-access">
          {status === 'PENDING' ? 'No portal access requests are waiting.' : 'No portal access requests match this filter.'}
        </p>
      )}
      {rows.length > 0 && (
        <ul className="space-y-3" aria-label="Portal access requests">
          {rows.map((request) => <AccessCard key={request.id} request={request} api={api} onChanged={changed} />)}
        </ul>
      )}
      {pagination && <RequestPager page={pagination.page} pages={pagination.pages} total={pagination.total} onPage={setPage} disabled={loading} />}
    </section>
  );
}
